import { Const } from '~/constants/Const.constant';
import operations from '~/helpers/device-operations';
import {
    command,
    command2,
    customer,
    firmware,
    id,
    lastSetup,
    lastUpdate,
    name,
    refurbished,
    room,
    setupDate,
    type
} from '~/helpers/fields-description';
import {
    AlgoType,
    ApplianceType,
    BatteryLevel,
    BehaviorFields,
    BehaviorKey,
    Command,
    Device,
    DeviceData,
    HeatingAlgoFields,
    NetworkFields,
    OffsetFields,
    OffsetKey,
    Operation,
    SetHeatingAlgoParams,
    SourceType,
} from '~/interfaces/device.interface';
import { FieldObject } from '~/interfaces/field.interface';
import { MappedOwner, OwnerType } from '~/interfaces/owners.interface';
import { specFieldsOrder } from '~/models/helpers/constants';
import apiService from '~/services/api.service';
import { ConfigModule } from '~/store/modules/config';
import { DevicesModule } from '~/store/modules/devices';
import { UserModule } from '~/store/modules/user';
import { LoadingModule } from '~common-store/modules/loading';
import { NotificationsModule } from '~common-store/modules/notifications';
import { PopupModule } from '~common-store/modules/popup';

export class NADevice {
    public static MAGENTO_CUSTOMER_IDS = [
        '1000019', // SAS FR
        '1000020', // SAS ROW
        '1000018', // LLC US
        '1000017', // LLC ROW
        'C00077', // NETATMO SAS SAV Europe
        'C00079', // NETATMO SAS SAV US
    ];
    // Default time before unknown, 3 hours
    MAX_TIME_BEFORE_UNKNOWN = 3 * 3600;
    // 10 minutes between last_temperature and last_seen
    MAX_TIME_BETWEEN_TEMP_SEEN = 10 * 60;
    MAX_TIME_BEFORE_CONSIDERED_DISCONNECTED = 0;
    // ten minutes offset to make sure the mainloop completion is taken into account
    MAINLOOP_OFFSET = 10 * 60;
    name = '';
    id = '';
    type = '';
    setup_date?: number;
    room_id?: string;
    subtype?: string;
    hw_version?: number;
    last_event_stored?: string;
    last_status_store?: number;
    last_seen?: number;
    date_setup?: number; // installation
    firmware?: number;
    firmware_private?: number;
    device_group?: string;
    command?: Command;
    command2?: Command;
    offset?: OffsetFields;
    homekit_migrated?: boolean;
    homekit_compatible?: boolean;
    homekit_paired?: boolean;
    station_name?: string;
    last_fw_update?: string;
    zigbee_address?: string;
    last_reset?: string;
    wifi_channel?: number;
    public_ext_data?: boolean;
    boiler_control?: string;
    dhw_control?: string;
    battery_vp?: number;
    oem_serial?: string;
    iccid?: string;
    uptime?: number;
    reset_next_install?: string;
    debug_enabled?: boolean;
    last_connection_status?: number;
    appliance_type?: ApplianceType;
    source_type?: SourceType;
    behavior: BehaviorFields = {
        led_disabled: false,
        legacy_offset: false,
        modified_rf_cycle_duration: false,
        relay_polarity_inverted: false,
        rf_statistics_enabled: false,
        touch_disabled: false,
        usb_host_disabled: false,
    };
    status?: string;
    network?: NetworkFields;
    reachable?: boolean;
    is_module = false;
    heating_algo?: HeatingAlgoFields;
    secret_key?: string = '';
    connection_time?: number;
    last_data?: number;
    friend_users_details?: OwnerType[];
    disabled_product?: boolean;
    refurb_date?: number;

    // Fields not received by backend.
    commandField?: string;
    commandField2?: string;
    commandLink?: string;

    hasSensitivityConfig?: boolean = false;
    hasVisionRecognitionConfig?: boolean = false;

    command2Field?: string;
    command2Field2?: string;
    command2Link?: string;

    homeId?: string;
    last_setup?: number;
    room?: string;
    behaviorFields: BehaviorKey[] = [];

    shouldFetchSyncData = false;
    shouldGetDebugConfigs = false;
    hasSwitchDebugMode = false;

    // operations
    hasOperationImportData = true;
    hasOperationMainloop = false;
    hasOperationReset = false;
    hasOperationDissociate = false;

    // used for switching database
    switchDatabaseAllowedGroups?: string[];

    hasModulesCount = false;

    statsDumpValue: { dump: string[], verbose: boolean } = { dump: [''], verbose: false };

    rf_levels: { [key: string]: number } = {
        full: 105,
        medium: 100,
    };
    // Manage levels
    battery_levels: any = {
        full: BatteryLevel.NtmoAPIBatteryLevel0,
        high: BatteryLevel.NtmoAPIBatteryLevel1,
        medium: BatteryLevel.NtmoAPIBatteryLevel2,
        low: BatteryLevel.NtmoAPIBatteryLevel3,
    };

    // Manage levels
    wifi_levels: { [key: string]: number } = {
        full: Const.NAWifiRssiThreshold.RSSI_THRESHOLD_2,
        high: Const.NAWifiRssiThreshold.RSSI_THRESHOLD_1,
        medium: Const.NAWifiRssiThreshold.RSSI_THRESHOLD_1,
    };

    isWired = true;

    specFieldsOrder = specFieldsOrder;

    constructor(data: DeviceData) {
        if (data) {
            this.setup_date = data.setup_date;
            this.loadData(data);
        }
    }

    get column() {
        return 'right';
    }

    get canBeConsideredDisconnected(): boolean {
        return false;
    }

    get orderedSpecFields() {
        return this.getSpecFields().sort((a, b) => {
            const aIndex = this.specFieldsOrder.indexOf(a.key);
            const bIndex = this.specFieldsOrder.indexOf(b.key);
            return aIndex >= 0 ? aIndex - bIndex : 1;
        });
    }

    get monkeyFields(): FieldObject[] | null {
        return null;
    }

    get nexelecFields(): FieldObject[] | null {
        return null;
    }

    get NSDFields(): FieldObject[] | null {
        return null;
    }

    get NCOFields(): FieldObject[] | null {
        return null;
    }

    get settingsFields(): FieldObject[] | null {
        return null;
    }

    get powerBoardFields(): FieldObject[] | null {
        return null;
    }

    get friendUsers() {
        return [] as MappedOwner[];
    }

    get deviceOperations(): Operation[] {
        const deviceOperations: Operation[] = [];
        if (this.hasOperationReset) {
            deviceOperations.push(operations.reset({ callback: this.reset }));
        }
        return deviceOperations;
    }

    get outdatedLastStatusStored() {
        return false;
    }

    get outdatedLastSeen() {
        return false;
    }

    get outdatedLastData() {
        return this.outdatedLastSeen
            || (this.last_data
                ? this.last_seen
                    ? Math.abs(this.last_data - this.last_seen) > this.MAX_TIME_BETWEEN_TEMP_SEEN
                    : false
                : true);
    }

    get isEnableSshDisabled() {
        return { value: false };
    }

    get isDissociateDisabled() {
        return { value: false, reason: '' };
    }

    get isFullResetDisabled() {
        return { value: false };
    }

    get isMainloopDisabled() {
        return { value: false };
    }

    get isOrderRebootDisabled() {
        return { value: false };
    }

    get isPingDisabled() {
        return { value: false };
    }

    get isStatsDumpDisabled() {
        return { value: false };
    }

    get isStatsDumpVerboseDisabled() {
        return { value: false };
    }

    get isSwitchDatabaseDisabled() {
        return { value: false };
    }

    get filteredBehavior() {
        if (this.behaviorFields?.length > 0) {
            const filteredBehavior = (Object.keys(this.behavior) as BehaviorKey[])
                .filter((key) => this.behaviorFields?.includes(key))
                .reduce((obj: BehaviorFields, key) => {
                    if (this.behavior) {
                        obj[key] = this.behavior[key];
                    }
                    return obj;
                }, {});
            return filteredBehavior;
        }
    }

    get deviceIdIncludesHash() {
        return this.id.includes('#');
    }

    get shouldShowDevice(): boolean {
        return this.deviceIdIncludesHash
            ? true
            // don't show device if at least another device exists with same id + #n
            : !DevicesModule.devices.some((device) => device.id.includes(`${this.id}#`));
    }

    get mainDevice(): Device | undefined {
        return DevicesModule.devices.find((device) => device.id === this.id.split('#')[0]);
    }

    get typeToDisplay(): string {
        return this.type;
    }

    loadData(data: any) {
        if (data.command) {
            this.commandField = `${data.command.customer_id} Retailer ${data.command.retailer_name}`;
            this.commandField2 = `Order ${data.command.order_id} ${data.command.sku}`;
            this.commandLink = `/${ConfigModule.supportAppNameBase}/order/${data.command.order_id}`;
        } else {
            this.commandField = 'N/A';
            this.commandField2 = 'N/A';
            this.commandLink = undefined;
        }

        if (data.command2) {
            this.command2Field = `${data.command2.customer_id} Retailer ${data.command2.retailer_name}`;
            this.command2Field2 = `Order ${data.command2.order_id} ${data.command2.sku}`;
            this.command2Link = `/${ConfigModule.supportAppNameBase}/order/${data.command2.order_id}`;
        }
        Object.assign(this, data);
    }

    loadOffset(offset: OffsetFields) {
        offset = offset || {};
        offset = {
            temperature: offset.temperature || 0,
            pressure: offset.pressure || 0,
            humidity_offset: offset.humidity_offset || 0,
            humidity: offset.humidity || 0,
        };
        this.offset = offset;
    }

    loadHeatingAlgo(heatingAlgo: HeatingAlgoFields) {
        this.heating_algo = heatingAlgo || {};
    }

    loadSyncData() {
        return;
    }

    getDebugConfigs() {
        return;
    }

    get fieldId(): FieldObject | undefined {
        return id(this.id);
    }
    get fieldName(): FieldObject | undefined {
        return name(this.name || this.mainDevice?.name);
    }
    get fieldType(): FieldObject | undefined {
        return type(this.type);
    }
    get fieldSetupDate(): FieldObject | undefined {
        return setupDate(this.setup_date || this.date_setup);
    }
    get fieldFirmware(): FieldObject | undefined {
        return firmware(this.firmware, this.type, this.device_group, this.hw_version, this.firmware_private);
    }
    get fieldLastUpdate(): FieldObject | undefined {
        return lastUpdate(this.last_fw_update);
    }
    get fieldLastSetup(): FieldObject | undefined {
        return lastSetup(this.last_setup);
    }
    get fieldRoom(): FieldObject | undefined {
        if (typeof this.room_id !== 'undefined') {
            return room(this.room_id);
        }
    }
    get fieldRefurbished(): FieldObject | undefined {
        if (this.refurb_date) {
            return refurbished(this.refurb_date);
        }
    }
    get fieldCustomer(): FieldObject | undefined {
        if (this.command) {
            return customer(this.commandField);
        }
    }
    get fieldCommand(): FieldObject | undefined {
        if (this.command) {
            return command(this.commandField2, this.commandLink);
        }
    }
    get fieldCustomer2(): FieldObject | undefined {
        if (this.command2) {
            return customer(this.command2Field);
        }
    }
    get fieldCommand2(): FieldObject | undefined {
        if (this.command2) {
            return command2(this.command2Field2, this.command2Link);
        }
    }
    get fieldUptime(): FieldObject | undefined {
        return undefined;
    }
    get fieldHwVersion(): FieldObject | undefined {
        return undefined;
    }
    get fieldLastStatusStored(): FieldObject | undefined {
        return undefined;
    }
    get fieldLastReset(): FieldObject | undefined {
        return undefined;
    }
    get fieldHomekitStatus(): FieldObject | undefined {
        return undefined;
    }
    get fieldZigBeeAddress(): FieldObject | undefined {
        return undefined;
    }
    get fieldSubtype(): FieldObject | undefined {
        return undefined;
    }
    get fieldMigrated(): FieldObject | undefined {
        return undefined;
    }
    get fieldFwPrivate(): FieldObject | undefined {
        return undefined;
    }
    get fieldTicStatus(): FieldObject | undefined {
        return undefined;
    }
    get fieldTicType(): FieldObject | undefined {
        return undefined;
    }
    get fieldOemSerial(): FieldObject | undefined {
        return undefined;
    }
    get fieldIccId(): FieldObject | undefined {
        return undefined;
    }
    get fieldMcuFirmware(): FieldObject | undefined {
        return undefined;
    }
    get fieldDeutschTelekomPortalLink(): FieldObject | undefined {
        return undefined;
    }
    get fieldRouterId(): FieldObject | undefined {
        return undefined;
    }
    get fieldWifiChip(): FieldObject | undefined {
        return undefined;
    }
    get fieldWifiChannel(): FieldObject | undefined {
        return undefined;
    }
    get fieldShield(): FieldObject | undefined {
        return undefined;
    }
    get fieldSdStatus(): FieldObject | undefined {
        return undefined;
    }
    get fieldMonitoring(): FieldObject | undefined {
        return undefined;
    }
    get fieldAlimStatus(): FieldObject | undefined {
        return undefined;
    }
    get fieldReconstructionStatus(): FieldObject | undefined {
        return undefined;
    }
    get fieldProtocolId(): FieldObject | undefined {
        return undefined;
    }
    get fieldBrandId(): FieldObject | undefined {
        return undefined;
    }
    get fieldOperatingMode(): FieldObject | undefined {
        return undefined;
    }
    get fieldProtocolList(): FieldObject | undefined {
        return undefined;
    }
    get fieldStationName(): FieldObject | undefined {
        return undefined;
    }
    get fieldShareWeathermap(): FieldObject | undefined {
        return undefined;
    }
    get fieldLastData(): FieldObject | undefined {
        return undefined;
    }
    get fieldWeatherMapIsFiltered(): FieldObject | undefined {
        return undefined;
    }
    get fieldWeatherMapFilteringReason(): FieldObject | undefined {
        return undefined;
    }
    get fieldRelayConnected(): FieldObject | undefined {
        return undefined;
    }
    get fieldInstallationVoltage(): FieldObject | undefined {
        return undefined;
    }
    get fieldInstallationIlim(): FieldObject | undefined {
        return undefined;
    }
    get fieldStateCharge(): FieldObject | undefined {
        return undefined;
    }
    get fieldStateOfHealth(): FieldObject | undefined {
        return undefined;
    }
    get fieldNbCharge(): FieldObject | undefined {
        return undefined;
    }
    get fieldBatteryTemperatureLevel(): FieldObject | undefined {
        return undefined;
    }
    get fieldStatus(): FieldObject | undefined {
        return undefined;
    }
    get fieldBatteryPercent(): FieldObject | undefined {
        return undefined;
    }
    get fieldKeyAttachments(): FieldObject | undefined {
        return undefined;
    }
    get fieldBoilerControl(): FieldObject | undefined {
        return undefined;
    }
    get fieldDhwControl(): FieldObject | undefined {
        return undefined;
    }
    get fieldBattery(): FieldObject | undefined {
        return undefined;
    }
    get fieldBubendorffMotorType(): FieldObject | undefined {
        return undefined;
    }
    get fieldLastSeen(): FieldObject | undefined {
        return undefined;
    }
    get fieldThermalControl(): FieldObject | undefined {
        return undefined;
    }
    get fieldConfigured(): FieldObject | undefined {
        return undefined;
    }
    get fieldMfgId(): FieldObject | undefined {
        return undefined;
    }
    get fieldApplianceType(): FieldObject | undefined {
        return undefined;
    }
    get fieldFullFeatureSupported(): FieldObject | undefined {
        return undefined;
    }
    get fieldPackaging(): FieldObject | undefined {
        return undefined;
    }
    get fieldErgonomics(): FieldObject | undefined {
        return undefined;
    }
    get fieldReachable(): FieldObject | undefined {
        return undefined;
    }
    get fieldMode(): FieldObject | undefined {
        return undefined;
    }
    get fieldButton(): FieldObject | undefined {
        return undefined;
    }
    get fieldControlledDevices(): FieldObject | undefined {
        return undefined;
    }
    get fieldControlledDevicesLeft(): FieldObject | undefined {
        return undefined;
    }
    get fieldControlledDevicesRight(): FieldObject | undefined {
        return undefined;
    }
    get fieldControlledDevicesMiddle(): FieldObject | undefined {
        return undefined;
    }
    get fieldLine(): FieldObject | undefined {
        return undefined;
    }
    get fieldSubline(): FieldObject | undefined {
        return undefined;
    }
    get fieldLineName(): FieldObject | undefined {
        return undefined;
    }
    get fieldEnergyType(): FieldObject | undefined {
        return undefined;
    }
    get fieldMullerType(): FieldObject | undefined {
        return undefined;
    }
    get fieldModel(): FieldObject | undefined {
        return undefined;
    }
    get fieldBrandName(): FieldObject | undefined {
        return undefined;
    }
    get fieldHeaterNature(): FieldObject | undefined {
        return undefined;
    }
    get fieldDevicePower(): FieldObject | undefined {
        return undefined;
    }
    get fieldManufacturer(): FieldObject | undefined {
        return undefined;
    }
    get fieldImage(): FieldObject | undefined {
        return undefined;
    }
    get fieldRfStatus(): FieldObject | undefined {
        return undefined;
    }
    get fieldLastActivity(): FieldObject | undefined {
        return undefined;
    }
    get fieldNotifyRule(): FieldObject | undefined {
        return undefined;
    }
    get fieldRecordRule(): FieldObject | undefined {
        return undefined;
    }
    get fieldRfRating(): FieldObject | undefined {
        return undefined;
    }
    get fieldSirenSoundRule(): FieldObject | undefined {
        return undefined;
    }
    get fieldSirenEntryDelay(): FieldObject | undefined {
        return undefined;
    }
    get fieldAutoTrigger(): FieldObject | undefined {
        return undefined;
    }
    get fieldTamperDetection(): FieldObject | undefined {
        return undefined;
    }
    get fieldStatusTampered(): FieldObject | undefined {
        return undefined;
    }
    get fieldAlimentation(): FieldObject | undefined {
        return undefined;
    }
    get fieldCalibrationMinMax(): FieldObject | undefined {
        return undefined;
    }
    get fieldLastCalibration(): FieldObject | undefined {
        return undefined;
    }
    get fieldMotorSpeed(): FieldObject | undefined {
        return undefined;
    }
    get fieldVoltageDrop(): FieldObject | undefined {
        return undefined;
    }
    get fieldBatteryDead(): FieldObject | undefined {
        return undefined;
    }
    get fieldPidAlgoPeriod(): FieldObject | undefined {
        return undefined;
    }
    get fieldLastResetType(): FieldObject | undefined {
        return undefined;
    }
    get fieldManualPairing(): FieldObject | undefined {
        return undefined;
    }
    get fieldEventualDoubleMac(): FieldObject | undefined {
        return undefined;
    }
    get fieldGroupIp(): FieldObject | undefined {
        return undefined;
    }
    get fieldVeluxType(): FieldObject | undefined {
        return undefined;
    }
    get fieldRadioId(): FieldObject | undefined {
        return undefined;
    }
    get fieldFeedbackGiven(): FieldObject | undefined {
        return undefined;
    }
    get fieldPowerSource(): FieldObject | undefined {
        return undefined;
    }

    getSpecFields(): FieldObject[] {
        return [
            this.fieldId,
            this.fieldName,
            this.fieldType,
            this.fieldSetupDate,
            this.fieldFirmware,
            this.fieldLastUpdate,
            this.fieldLastSetup,
            this.fieldCustomer,
            this.fieldCommand,
            this.fieldCustomer2,
            this.fieldCommand2,
            this.fieldRoom,
            this.fieldRefurbished,
            this.fieldUptime,
            this.fieldHwVersion,
            this.fieldLastStatusStored,
            this.fieldLastReset,
            this.fieldHomekitStatus,
            this.fieldZigBeeAddress,
            this.fieldSubtype,
            this.fieldMigrated,
            this.fieldFwPrivate,
            this.fieldTicStatus,
            this.fieldTicType,
            this.fieldOemSerial,
            this.fieldIccId,
            this.fieldMcuFirmware,
            this.fieldDeutschTelekomPortalLink,
            this.fieldRouterId,
            this.fieldWifiChip,
            this.fieldShield,
            this.fieldWifiChannel,
            this.fieldSdStatus,
            this.fieldMonitoring,
            this.fieldAlimStatus,
            this.fieldReconstructionStatus,
            this.fieldProtocolId,
            this.fieldBrandId,
            this.fieldOperatingMode,
            this.fieldProtocolList,
            this.fieldStationName,
            this.fieldShareWeathermap,
            this.fieldLastData,
            this.fieldWeatherMapIsFiltered,
            this.fieldWeatherMapFilteringReason,
            this.fieldRelayConnected,
            this.fieldInstallationVoltage,
            this.fieldInstallationIlim,
            this.fieldStateCharge,
            this.fieldStateOfHealth,
            this.fieldNbCharge,
            this.fieldBatteryTemperatureLevel,
            this.fieldStatus,
            this.fieldBatteryPercent,
            this.fieldKeyAttachments,
            this.fieldBoilerControl,
            this.fieldDhwControl,
            this.fieldBattery,
            this.fieldBubendorffMotorType,
            this.fieldLastSeen,
            this.fieldThermalControl,
            this.fieldConfigured,
            this.fieldMfgId,
            this.fieldApplianceType,
            this.fieldFullFeatureSupported,
            this.fieldPackaging,
            this.fieldErgonomics,
            this.fieldReachable,
            this.fieldMode,
            this.fieldButton,
            this.fieldControlledDevices,
            this.fieldControlledDevicesLeft,
            this.fieldControlledDevicesRight,
            this.fieldControlledDevicesMiddle,
            this.fieldLine,
            this.fieldSubline,
            this.fieldLineName,
            this.fieldEnergyType,
            this.fieldMullerType,
            this.fieldModel,
            this.fieldBrandName,
            this.fieldHeaterNature,
            this.fieldDevicePower,
            this.fieldManufacturer,
            this.fieldImage,
            this.fieldRfStatus,
            this.fieldLastActivity,
            this.fieldNotifyRule,
            this.fieldRecordRule,
            this.fieldRfRating,
            this.fieldSirenSoundRule,
            this.fieldSirenEntryDelay,
            this.fieldAutoTrigger,
            this.fieldTamperDetection,
            this.fieldStatusTampered,
            this.fieldAlimentation,
            this.fieldCalibrationMinMax,
            this.fieldLastCalibration,
            this.fieldMotorSpeed,
            this.fieldVoltageDrop,
            this.fieldBatteryDead,
            this.fieldPidAlgoPeriod,
            this.fieldLastResetType,
            this.fieldManualPairing,
            this.fieldEventualDoubleMac,
            this.fieldGroupIp,
            this.fieldVeluxType,
            this.fieldRadioId,
            this.fieldFeedbackGiven,
            this.fieldPowerSource,
        ].filter((field) => !!field);
    }

    getNetworkFields(): FieldObject[] {
        return [];
    }

    getRfIcon(status?: number) {
        // rf_amb_status is the ambiant noise, it's a negative value but stocked positive.
        // That's why we look it above threshold
        for (const threshold in this.rf_levels) {
            if (status && status > this.rf_levels[threshold]) {
                return `signal_station_${threshold}`;
            }
        }
        if (!status) {
            return 'signal_station_unknown';
        }
        return 'signal_station_low';
    }

    getWifiIcon(status?: string | number) {
        if (status === 'ethernet') {
            return status + '-ok';
        }
        for (const threshold in this.wifi_levels) {
            if (status && Number(status) < this.wifi_levels[threshold]) {
                return `wifi_${threshold}`;
            }
        }
        if (!status) {
            return 'wifi_unknown';
        }
        return 'wifi_low';
    }

    enableSsh = async () => {
        await this.handleOperation();
    };

    deleteCache = async () => {
        await this.handleOperation({
            callback: () => apiService.admindeletedevicecache({ device_id: this.id }),
            confirmText: 'You are about to delete the cache of this device, do you want to continue ?',
        });
    };

    dissociate = async () => {
        await this.handleOperation();
    };

    dump = async () => {
        try {
            LoadingModule.showLoading(true);
            return (await apiService.admingetdevicedump({ device_id: this.id }) as any).data.body.dump;
        } catch (error) {
            return 'Error while fetching dump';
        } finally {
            PopupModule.setLastExecuted(new Date());
            LoadingModule.showLoading(false);
        }
    };

    dumpLogs = async () => {
        try {
            LoadingModule.showLoading(true);
            return (await apiService.admingetdevicedumplogs({ device_id: this.id }) as any).data.body.dumpLogs;
        } catch (error) {
            return 'Error while fetching dump logs';
        } finally {
            PopupModule.setLastExecuted(new Date());
            LoadingModule.showLoading(false);
        }
    };

    fullReset = async () => {
        await this.handleOperation();
    };

    getOEMInfos = async () => {
        await this.handleOperation({
            callback: () => apiService.getOEMInfos({ device_id: this.id }),
        });
    };

    importData = async (params: { device_id: string, type: string }) => {
        try {
            LoadingModule.showLoading(true);

            const newMigrationData = ((await apiService.adminimportmigrationdata2({
                target_bridge_id: params.device_id,
                user_id: UserModule.id,
            })).data.body);

            return newMigrationData;
        } catch (error) {
            return 'Error while fetching migration data';
        } finally {
            LoadingModule.showLoading(false);
        }
    };

    installQRCode = async () => {
        await this.handleOperation();
    };

    logs = async () => {
        try {
            LoadingModule.showLoading(true);
            return (await apiService.admingetdevicelogs({ device_id: this.id }) as any).data.body.logs;
        } catch (error) {
            return 'Error while fetching logs';
        } finally {
            PopupModule.setLastExecuted(new Date());
            LoadingModule.showLoading(false);
        }
    };

    mainloop = async () => {
        await this.handleOperation({
            callback: () => apiService.adminaskplugtoreconnect({ device_id: this.id }),
            confirmText: 'You are about to force main loop on this device, do you want to continue ?',

        });
    };

    modelDump = async () => {
        try {
            LoadingModule.showLoading(true);
            return (await apiService.dumpexternalmemory({ device_id: this.id }) as any).data.body.dump;
        } catch (error) {
            return 'Error while fetching model dump';
        } finally {
            PopupModule.setLastExecuted(new Date());
            LoadingModule.showLoading(false);
        }
    };

    getDiagnosis = async () => {
        try {
            LoadingModule.showLoading(true);
            const base64Diagnosis = (await apiService.getdiagnosis({ device_id: this.id, option: 'dump_charging_controller_logs' }) as any).data.body.diagnosis;
            return atob(base64Diagnosis);
        } catch (error) {
            return 'Error while fetching diagnosis';
        } finally {
            PopupModule.setLastExecuted(new Date());
            LoadingModule.showLoading(false);
        }
    };

    orderReboot = async () => {
        await this.handleOperation({
            callback: () => apiService.adminorderdevicereboot({ device_id: this.id }),
            confirmText: 'You are about to order the device to reboot, do you want to continue ?',
        });
    };

    ping = async () => {
        await this.handleOperation();
    };

    recalibrateCO2 = async () => {
        await this.handleOperation({
            callback: () => apiService.recalibrateCO2({ device_id: this.id }),
            confirmText: 'You are about to recalibrate the CO2 of this device, do you want to continue ?',
        });
    };

    recomputeHeating = async () => {
        await this.handleOperation({
            callback: () => apiService.adminrecomputeheatingalgo({ device_id: this.id }),
        });
    };

    async reset(params?: { device_id: string, module_id?: string }) {
        if (!params) {
            return;
        }
        try {
            await this.handleOperation({
                callback: () => apiService.adminresetdevice(params),
                confirmText: 'You are about to completely reset this device, do you want to continue ?',
            });
            // re-search device after reset is complete
            window.location.reload();
        } catch (error) {
            return;
        }
    }

    resetQRCode = async () => {
        await this.handleOperation();
    };

    setDump = async () => {
        await this.handleOperation({
            callback: () => apiService.admintoggledevicedump({ device_id: this.id }),
            confirmText: 'You are about to change the dump value of this device, do you want to continue ?',
        });
    };

    statsDump: () => Promise<any> = async () => {
        await this.handleOperation();
    };

    statsDumpVerbose: () => Promise<any> = async () => {
        await this.handleOperation();
    };

    statsDumpFunction = ({ verbose = false }: { verbose: boolean }) => {
        return Promise.resolve({ dump: '', verbose });
    };

    switchDatabase = async () => {
        const target = ConfigModule.environment === 'production'
            ? 'netcomv2.inte.netatmo.net'
            : `nv2-${this.type.toLowerCase()}.netatmo.net`;
        await this.handleOperation({
            callback: () => apiService.switchDatabase({ device_id: this.id, target }),
            confirmText: 'To switch the database your device first needs to be declared in the concerned database',
        });
        return;
    };

    triggerLogsDump = async () => {
        await this.handleOperation({
            callback: () => apiService.setdebugconfigs(
                {
                    device_id: this.id,
                    options: {
                        home: {
                            modules: [{
                                id: this.id,
                                dump_logs: true,
                            }],
                        },
                    },
                },
                'Logs dump trigger successfully requested',
            ),
        });
    };

    setDebugValue = async (value: boolean) => {
        await this.handleOperation({
            callback: () => apiService.adminupdatedevice({
                device_id: this.id,
                debug_enabled: value,
            }, 'Toggle debug mode successful'),
        });
    };

    timeline = async () => {
        await this.handleOperation();
    };

    unlock = async () => {
        await this.handleOperation();
    };

    resetFlag = async () => {
        await this.handleOperation({
            callback: () => apiService.resetflag({ device_id: this.id }),
            confirmText: 'You are about to reset flag for this device, do you want to continue ?',
        });
    };

    async setBehavior(behavior: BehaviorFields) {
        const mappedBehavior = Object.entries(behavior).map((entry) => {
            entry[1] = entry[1] ? 1 : 0;
            return entry;
        });
        const params = {
            device_id: this.id,
            ...Object.fromEntries(mappedBehavior),
        };
        await apiService.setBehavior(params, 'Behavior successfully updated');
        DevicesModule.setBehavior({ deviceId: this.id, behavior });
    }

    async setOffset(gatewayId: string, targetId: string, offsetKey: OffsetKey, offsetValue: number) {
        const params = {
            device_id: gatewayId,
            module_id: targetId,
            data_type: offsetKey,
            offset: offsetValue,
        };

        await apiService.setOffset(params, 'Offsets successfully updated');
        DevicesModule.setOffset({ deviceId: targetId, offsetKey, offsetValue });

    }

    async setHeatingAlgoType(heatingAlgoType: AlgoType): Promise<void> {
        if (this.heating_algo) {
            DevicesModule.setHeatingAlgoType({ deviceId: this.id, heatingAlgoType });
            const params = { algo_type: heatingAlgoType, device_id: this.id } as SetHeatingAlgoParams;
            if (heatingAlgoType === 'simple_algo') {
                params.algo_params = this.heating_algo.simple_params || { high_deadband: 0 };
            } else {
                params.algo_params = this.heating_algo.pid_params || { gain: 0, ti: 0, td: 0, period: 0 };
            }
            await apiService.setHeatingAlgo(params, 'Heating algo successfully updated');
        }
    }

    async getDeviceSecret() {
        const deviceSecret = (await apiService.getdevicesecret({ device_id: this.id })).data.body.secret_key as string;
        DevicesModule.setDeviceSecret({ deviceId: this.id, deviceSecret });
    }

    async handleOperation(params?: { callback: () => Promise<any>, confirmText?: string }) {
        if (!params) {
            NotificationsModule.pushNotification({
                title: 'Warning',
                text: 'Operation not handled for this device type',
                type: 'warning',
            });
        } else {
            if (!params.confirmText || (params.confirmText && confirm(params.confirmText))) {
                try {
                    LoadingModule.showLoading(true);
                    await params.callback();
                } finally {
                    LoadingModule.showLoading(false);
                }
            }
        }
    }

    getBatteryIcon(status?: number) {
        for (const threshold in this.battery_levels) {
            if (status && status > this.battery_levels[threshold]) {
                return `battery_${threshold}`;
            }
        }
        if (!status) {
            return 'battery_unknown';
        }
        return 'battery_verylow';
    }

    getBoolFieldClass(bool?: boolean): string {
        return bool ? 'success--text' : 'error--text';
    }
}
